import React from 'react';
import {
  FilePlus,
  CheckCircle,
  CreditCard,
  Send,
  AlertCircle,
  Clock,
  Loader2,
  History,
  Trash2,
} from 'lucide-react';
import { PrescriptionAuditLog } from '@/lib/types';
import { formatToLocalDate } from '@/lib/utils';
import { motion } from 'framer-motion';

interface PrescriptionAuditTrailProps {
  auditLogs: PrescriptionAuditLog[];
  isLoading?: boolean;
}

const PrescriptionAuditTrail: React.FC<PrescriptionAuditTrailProps> = ({
  auditLogs,
  isLoading = false,
}) => {
  const getActionIcon = (action: PrescriptionAuditLog['action']) => {
    switch (action) {
      case 'created':
        return <FilePlus className='h-3.5 w-3.5' />;
      case 'verified':
        return <CheckCircle className='h-3.5 w-3.5' />;
      case 'payment_processed':
        return <CreditCard className='h-3.5 w-3.5' />;
      case 'sent_to_pharmacy':
        return <Send className='h-3.5 w-3.5' />;
      case 'status_changed':
        return <AlertCircle className='h-3.5 w-3.5' />;
      case 'medication_removed':
        return <Trash2 className='h-3.5 w-3.5' />;
      default:
        return <Clock className='h-3.5 w-3.5' />;
    }
  };

  const getActionColor = (action: PrescriptionAuditLog['action']) => {
    switch (action) {
      case 'created':
        return 'text-blue-500';
      case 'verified':
        return 'text-emerald-500';
      case 'payment_processed':
        return 'text-violet-500';
      case 'sent_to_pharmacy':
        return 'text-orange-500';
      case 'status_changed':
        return 'text-slate-400';
      case 'medication_removed':
        return 'text-red-500';
      default:
        return 'text-slate-400';
    }
  };

  const getActionLabel = (log: PrescriptionAuditLog) => {
    if (log.action === 'medication_removed') {
      const medicationName = String(log.metadata?.medication_name || '').trim();
      const medicationDose = String(log.metadata?.medication_dose || '').trim();
      if (medicationName) {
        return `Medication Removed (${medicationName}${medicationDose ? ` ${medicationDose}` : ''})`;
      }
      return 'Medication Removed';
    }

    switch (log.action) {
      case 'created':
        return 'Created';
      case 'verified':
        return 'Verified';
      case 'payment_processed':
        return 'Payment processed';
      case 'sent_to_pharmacy':
        return 'Sent to pharmacy';
      case 'status_changed':
        return 'Status changed';
      default:
        return log.action;
    }
  };

  const formatName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed.includes('@')) return trimmed;
    return trimmed
      .split(/\s+/)
      .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
      .join(' ');
  };

  const getPerformedBy = (log: PrescriptionAuditLog) => {
    const user = log.performed_by_user;
    const userFullName =
      user?.full_name ||
      (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '');
    const performedBy =
      log.performed_by_practitioner?.full_name ||
      userFullName ||
      user?.email ||
      'System';
    return formatName(performedBy);
  };

  if (isLoading) {
    return (
      <div className='rounded-xl border border-slate-200 bg-white'>
        <div className='px-4 py-3 border-b border-slate-100'>
          <h3 className='text-sm font-semibold text-slate-900'>Audit Trail</h3>
        </div>
        <div className='p-8 flex items-center justify-center'>
          <Loader2 className='h-5 w-5 animate-spin text-slate-300' />
        </div>
      </div>
    );
  }

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <div className='rounded-xl border border-slate-200 bg-white'>
        <div className='px-4 py-3 border-b border-slate-100'>
          <h3 className='text-sm font-semibold text-slate-900'>Audit Trail</h3>
        </div>
        <div className='p-8 text-center'>
          <History className='h-8 w-8 text-slate-200 mx-auto mb-2' />
          <p className='text-sm text-slate-400'>No activity yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-xl border border-slate-200 bg-white'>
      <div className='px-4 py-3 border-b border-slate-100'>
        <h3 className='text-sm font-semibold text-slate-900'>Audit Trail</h3>
      </div>
      
      <div className='p-4'>
        <div className='space-y-0'>
          {auditLogs.map((log, index) => {
            const isLast = index === auditLogs.length - 1;

            return (
              <motion.div
                key={log.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15, delay: index * 0.02 }}
                className='relative'
              >
                {/* Timeline line */}
                {!isLast && (
                  <div className='absolute left-3 top-6 bottom-0 w-px bg-slate-200 -translate-x-1/2' />
                )}
                
                <div className='flex gap-3 py-2.5'>
                  {/* Timeline dot */}
                  <div className={`w-6 h-6 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center flex-shrink-0 relative z-10 ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>

                  {/* Content */}
                  <div className='flex-1 min-w-0 pt-0.5'>
                    <div className='flex items-baseline justify-between gap-2'>
                      <div className='flex items-baseline gap-1.5 flex-wrap'>
                        <span className='text-sm text-slate-700 font-medium'>{getPerformedBy(log)}</span>
                        <span className='text-xs text-slate-400'>{getActionLabel(log)}</span>
                      </div>
                      <span className='text-[11px] text-slate-400 flex-shrink-0'>
                        {formatToLocalDate(log.created_at, 'MM/DD/YY h:mm a')}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionAuditTrail;
